// src/api/analytics.ts
import { fetchJson } from './apiClient';
import { resolveApiBase } from '../utils/apiBase';

const API_BASE = resolveApiBase();

export type UserMetric =
  | 'views'
  | 'shares'
  | 'contactClicks'
  | 'messageClicks'
  | 'socialClicks'
  | 'saves'
  | 'qrScans'
  // legacy aliases accepted server-side
  | 'emailClicks'
  | 'callClicks'
  | 'websiteClicks'
  | 'shareClicks';

/**
 * Legacy client helper for incrementing user metrics.
 *
 * Prefer using services/analytics.incrementMetric from the new service layer.
 * This function remains for backward compatibility and attempts the new endpoint first,
 * falling back to the legacy route if necessary.
 */
export async function incrementUserMetric(idOrUsername: string, metric: UserMetric) {
  // 1) Try new-style endpoint: POST /users/:id/metrics/:metric
  const tryNew = async () => {
    const url = `${API_BASE}/users/${encodeURIComponent(idOrUsername)}/metrics/${encodeURIComponent(metric)}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    return res;
  };
  // 2) Fallback to legacy endpoint: POST /users/:id/analytics/increment { metric }
  const tryLegacy = async () => {
    const url = `${API_BASE}/users/${encodeURIComponent(idOrUsername)}/analytics/increment`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ metric })
    });
    return res;
  };

  let res = await tryNew();
  if (!res.ok && res.status === 404) {
    // Server might not support the new route yet; fall back seamlessly
    res = await tryLegacy();
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw Object.assign(new Error(err?.error || 'Failed to increment metric'), { status: res.status });
  }
  const body = await res.json().catch(() => ({}));
  // Broadcast lightweight analytics update for same-origin tabs so owner dashboards can refresh in near-real-time
  try {
    if (typeof window !== 'undefined' && body) {
      const analytics = (body as any).analytics || (body as any).data || body;
      if (analytics) {
        try {
          const payload = JSON.stringify({ userId: String(idOrUsername), analytics, ts: Date.now() });
          // single key replace so storage events fire in other tabs
          localStorage.setItem('tcards_analytics_update', payload);
        } catch {}
      }
    }
  } catch {}
  return body;
}

export async function track(userId: string, metric: string, payload?: any) {
  return fetchJson<void>(`/api/analytics/${encodeURIComponent(userId)}`, {
    method: 'POST',
    body: JSON.stringify({ metric, payload, ts: Date.now() }),
  });
}

export async function getSummary(userId: string, range: string = '7d') {
  // keep this shape for later when backend implements it
  return fetchJson(`/api/analytics/summary?userId=${encodeURIComponent(userId)}&range=${encodeURIComponent(range)}`);
}

export async function trackAdView(adId: string) {
  try {
    if (typeof window !== 'undefined') {
      const p = window.location?.pathname || '';
      // don't ping ads in admin/auth/studio/dashboard/template manager views
      if (p.startsWith('/auth')) return { ok: false } as any;
      if (/(admin|studio|dashboard|template|manager)/i.test(p)) return { ok: false } as any;
    }
  } catch {}
  // Back-compat: map simple numeric mock ids to fallback pattern used server-side
  const mapped = (/^\d+$/.test(adId)) ? `fallback-${adId}` : adId;
  const url = `${API_BASE}/ads/${encodeURIComponent(mapped)}/view`;
  const res = await fetch(url, { method: 'POST' });
  if (!res.ok) return { ok: false } as any;
  return res.json();
}

export async function trackAdClick(adId: string) {
  try {
    if (typeof window !== 'undefined') {
      const p = window.location?.pathname || '';
      // don't ping ads in admin/auth/studio/dashboard/template manager views
      if (p.startsWith('/auth')) return { ok: false } as any;
      if (/(admin|studio|dashboard|template|manager)/i.test(p)) return { ok: false } as any;
    }
  } catch {}
  const mapped = (/^\d+$/.test(adId)) ? `fallback-${adId}` : adId;
  const url = `${API_BASE}/ads/${encodeURIComponent(mapped)}/click`;
  const res = await fetch(url, { method: 'POST' });
  if (!res.ok) return { ok: false } as any;
  return res.json();
}

// Build a redirect URL for ads that will increment redirects server-side before navigating
export function adRedirectUrl(adId: string, fallbackTarget?: string) {
  // Prefer /api alias so it works behind reverse proxy on the static host
  const to = String(fallbackTarget || '').trim();
  const qp = to ? `?to=${encodeURIComponent(to)}` : '';
  const mapped = (/^\d+$/.test(adId)) ? `fallback-${adId}` : adId;
  return `${API_BASE}/ads/${encodeURIComponent(mapped)}/redirect${qp}`;
}
